import React, { useState } from "react";
import {
  Modal,
  Button,
  Text,
  Table,
  ScrollArea,
  Badge,
  Flex,
  Alert,
  Group,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { IconUpload, IconX, IconFileSpreadsheet } from "@tabler/icons";
import Papa from "papaparse";
import {
  useApiUrl,
  useCustomMutation,
  useInvalidate,
} from "@refinedev/core";
import { REASON_OPTIONS } from "entities/review_request_blacklist";

interface ParsedRow {
  email: string;
  reason?: string;
  source_detail?: string;
}

interface RowError {
  row: number;
  message: string;
}

const VALID_REASONS = REASON_OPTIONS.map((o) => o.value);

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateRows(rows: ParsedRow[]): RowError[] {
  const errors: RowError[] = [];
  rows.forEach((row, i) => {
    if (!row.email || !row.email.trim()) {
      errors.push({ row: i + 1, message: "Email is required" });
    } else if (!validateEmail(row.email.trim())) {
      errors.push({
        row: i + 1,
        message: `Invalid email: ${row.email}`,
      });
    }
    if (row.reason && !VALID_REASONS.includes(row.reason.trim())) {
      errors.push({
        row: i + 1,
        message: `Unknown reason: ${row.reason}`,
      });
    }
  });
  return errors;
}

interface ImportCsvModalProps {
  opened: boolean;
  onClose: () => void;
}

export const ImportCsvModal: React.FC<ImportCsvModalProps> = ({
  opened,
  onClose,
}) => {
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [errors, setErrors] = useState<RowError[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  const apiUrl = useApiUrl();
  const invalidate = useInvalidate();
  const { mutate: importMutate, isLoading: isImporting } =
    useCustomMutation();

  const reset = () => {
    setParsedRows([]);
    setErrors([]);
    setParseError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDrop = (files: File[]) => {
    setParseError(null);
    const file = files[0];
    if (!file) return;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete(results) {
        const fields = results.meta.fields ?? [];
        if (!fields.includes("email")) {
          setParseError('CSV must contain an "email" column.');
          setParsedRows([]);
          setErrors([]);
          return;
        }

        const rows: ParsedRow[] = results.data.map((d) => ({
          email: (d.email ?? "").trim(),
          reason: (d.reason ?? "").trim() || undefined,
          source_detail: (d.source_detail ?? "").trim() || undefined,
        }));

        const validationErrors = validateRows(rows);
        setParsedRows(rows);
        setErrors(validationErrors);
      },
      error(err) {
        setParseError(`Failed to parse CSV: ${err.message}`);
      },
    });
  };

  const handleSubmit = () => {
    importMutate(
      {
        url: `${apiUrl}/review_request_blacklists/import`,
        method: "post",
        values: { entries: parsedRows },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "review_request_blacklists",
            invalidates: ["list"],
          });
          handleClose();
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import Blacklist from CSV"
      size="lg"
    >
      {parsedRows.length === 0 ? (
        <>
          {parseError && (
            <Alert color="red" mb="sm">
              {parseError}
            </Alert>
          )}
          <Dropzone
            onDrop={handleDrop}
            accept={[MIME_TYPES.csv]}
            maxFiles={1}
          >
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="xl"
              gap="sm"
            >
              <Dropzone.Accept>
                <IconUpload size={40} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={40} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFileSpreadsheet size={40} stroke={1.5} />
              </Dropzone.Idle>
              <Text size="sm" color="dimmed" align="center">
                Drag a CSV file here or click to browse.
                <br />
                Required column: <b>email</b>. Optional: <b>reason</b>,{" "}
                <b>source_detail</b>.
              </Text>
            </Flex>
          </Dropzone>
        </>
      ) : (
        <>
          {errors.length > 0 && (
            <Alert color="red" mb="sm" title="Validation errors">
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {errors.map((e, i) => (
                  <li key={i}>
                    Row {e.row}: {e.message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <Text size="sm" mb="xs" weight={500}>
            Preview ({parsedRows.length} rows)
          </Text>

          <ScrollArea style={{ maxHeight: 300 }}>
            <Table fontSize="xs" highlightOnHover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Email</th>
                  <th>Reason</th>
                  <th>Source Detail</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, i) => {
                  const rowErrors = errors.filter((e) => e.row === i + 1);
                  return (
                    <tr
                      key={i}
                      style={
                        rowErrors.length > 0
                          ? { backgroundColor: "rgba(255,0,0,0.05)" }
                          : undefined
                      }
                    >
                      <td>{i + 1}</td>
                      <td>{row.email}</td>
                      <td>
                        {row.reason ? (
                          <Badge size="xs">{row.reason}</Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{row.source_detail || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ScrollArea>

          <Group position="apart" mt="md">
            <Button variant="subtle" color="gray" onClick={reset}>
              Choose another file
            </Button>
            <Group spacing="sm">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={isImporting}
                disabled={errors.length > 0}
              >
                Import {parsedRows.length} rows
              </Button>
            </Group>
          </Group>
        </>
      )}
    </Modal>
  );
};
